import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { StatusPill, CountdownChip, fmtDate, VENDOR_RESPONSE_HOURS, VENDOR_FULFIL_DAYS } from "./ReturnsShared";

export default function VendorReturnsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decideFor, setDecideFor] = useState(null); // return doc
  const [decision, setDecision] = useState("accepted");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/exhibitor/returns");
      setRows(data || []);
    } catch { toast.error("Could not load return requests"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const openDecide = (r) => { setDecideFor(r); setDecision("accepted"); setNote(""); };

  const submitDecision = async () => {
    if (!decideFor) return;
    setBusy(true);
    try {
      await api.post(`/returns/${decideFor.id}/vendor-decision`, { decision, note });
      toast.success(decision === "accepted" ? "Accepted. Please fulfil within 5 days." : "Rejected. Buyer will be notified.");
      setDecideFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit decision"); }
    finally { setBusy(false); }
  };

  const markFulfilled = async (r) => {
    if (!window.confirm("Mark this request as completed (replacement shipped or refund processed)?")) return;
    try {
      await api.post(`/returns/${r.id}/fulfil`);
      toast.success("Marked completed.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const pending = rows.filter((r) => r.status === "pending_vendor");
  const accepted = rows.filter((r) => r.status === "accepted");
  const closed = rows.filter((r) => !["pending_vendor", "accepted"].includes(r.status));

  const Row = ({ r }) => (
    <div key={r.id} className="p-4 rounded-2xl bg-white border border-gray-200" data-testid={`vendor-return-${r.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        {r.product_image && <img src={r.product_image} alt="" className="h-16 w-16 rounded-md object-cover" />}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{r.product_name}</div>
          <div className="text-xs text-gray-500">Order #{(r.order_id || "").slice(0, 8).toUpperCase()} · {r.kind === "replacement" ? "Replacement" : "Refund"} · Raised {fmtDate(r.created_at)}</div>
          <div className="mt-1 text-sm text-gray-700">{r.reason}</div>
          {r.photo_base64 && <img src={r.photo_base64} alt="proof" className="mt-2 h-24 w-24 rounded-md object-cover border border-gray-200" />}
        </div>
        <div className="text-right space-y-1">
          <StatusPill status={r.status} testid={`vendor-return-status-${r.id}`} />
          {r.status === "pending_vendor" && <CountdownChip deadlineIso={r.vendor_deadline} testid={`vendor-return-clock-${r.id}`} prefix={`${VENDOR_RESPONSE_HOURS}h response`} />}
          {r.status === "accepted" && <CountdownChip deadlineIso={r.fulfil_deadline} testid={`vendor-fulfil-clock-${r.id}`} prefix={`${VENDOR_FULFIL_DAYS}d fulfil`} />}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {r.status === "pending_vendor" && (
          <Button data-testid={`vendor-return-decide-${r.id}`} onClick={() => openDecide(r)} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white h-9 px-4 text-xs">
            Accept or reject
          </Button>
        )}
        {r.status === "accepted" && (
          <Button data-testid={`vendor-return-fulfil-${r.id}`} onClick={() => markFulfilled(r)} variant="outline" className="rounded-full h-9 px-4 text-xs">
            Mark completed
          </Button>
        )}
        {r.vendor_note && <div className="text-xs text-gray-500">Your note: <span className="text-gray-700">{r.vendor_note}</span></div>}
      </div>
    </div>
  );

  if (loading) return <div className="text-sm text-gray-500 py-10 text-center">Loading return requests…</div>;

  return (
    <div className="space-y-6" data-testid="vendor-returns-tab">
      <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-sm leading-relaxed">
        Buyers can raise return or replacement requests within 5 days of delivery. You have {VENDOR_RESPONSE_HOURS} hours to accept or reject each request. Once accepted, pickup or replacement must complete within {VENDOR_FULFIL_DAYS} days. Missed windows are auto-escalated to CityShor Support.
      </div>

      <section>
        <div className="font-display text-xl mb-3">Awaiting your response ({pending.length})</div>
        {pending.length === 0 ? (
          <div className="text-sm text-gray-500 py-10 text-center border border-dashed border-gray-200 rounded-2xl">Nothing to action right now.</div>
        ) : <div className="space-y-3">{pending.map((r) => <Row key={r.id} r={r} />)}</div>}
      </section>

      <section>
        <div className="font-display text-xl mb-3">In fulfilment ({accepted.length})</div>
        {accepted.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-2xl">No accepted requests are being processed.</div>
        ) : <div className="space-y-3">{accepted.map((r) => <Row key={r.id} r={r} />)}</div>}
      </section>

      {closed.length > 0 && (
        <section>
          <div className="font-display text-xl mb-3">History ({closed.length})</div>
          <div className="space-y-3">{closed.map((r) => <Row key={r.id} r={r} />)}</div>
        </section>
      )}

      <Dialog open={!!decideFor} onOpenChange={(v) => !v && setDecideFor(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Respond to request</DialogTitle></DialogHeader>
          {decideFor && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-gray-500">{decideFor.product_name} · {decideFor.kind}</div>
              <div className="grid grid-cols-2 gap-2">
                <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${decision === "accepted" ? "border-green-400 bg-green-50" : "border-gray-200"}`}>
                  <input type="radio" checked={decision === "accepted"} onChange={() => setDecision("accepted")} data-testid="vendor-decide-accept" className="accent-green-600" />
                  <span>Accept</span>
                </label>
                <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${decision === "rejected" ? "border-red-400 bg-red-50" : "border-gray-200"}`}>
                  <input type="radio" checked={decision === "rejected"} onChange={() => setDecision("rejected")} data-testid="vendor-decide-reject" className="accent-red-600" />
                  <span>Reject</span>
                </label>
              </div>
              <Textarea data-testid="vendor-decide-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={decision === "accepted" ? "Optional note to the buyer (pickup window, replacement colour, etc.)" : "Reason for rejection — required for our records"} />
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setDecideFor(null)} disabled={busy} className="rounded-full">Cancel</Button>
                <Button data-testid="vendor-decide-submit" onClick={submitDecision} disabled={busy || (decision === "rejected" && !note.trim())} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white">
                  {busy ? "Saving…" : "Submit"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
